import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Copy, Play, MessageSquare, FileCode } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import CodeHighlighter from './SyntaxHighlighter';
import CodeHistory from './CodeHistory';
import { CodeSnippet } from '@/components/chat/ChatDialogs';

interface CodeEditorProps {
  onSave: (code: string, language: string) => void;
  onRun?: (code: string, language: string) => void;
  onClose?: () => void;
  onAskForCode?: (prompt: string, language: string) => Promise<string>;
  codeSnippets?: CodeSnippet[];
  onSelectSnippet?: (snippetId: string) => void;
  showHistory?: boolean;
  initialCode?: string;
  initialLanguage?: string;
  onInsertSnippetToChat?: (snippet: CodeSnippet) => void; // NEW PROP
}

function stripMarkdownCodeFencing(raw: string): string {
  const codeBlockRegex = /^```(?:\w+)?\n([\s\S]*?)\n```$/;
  const match = raw.match(codeBlockRegex);
  return match ? match[1] : raw;
}

const languages = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'python', label: 'Python' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'java', label: 'Java' },
  { value: 'c', label: 'C' },
  { value: 'cpp', label: 'C++' },
  { value: 'csharp', label: 'C#' },
  { value: 'go', label: 'Go' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'rust', label: 'Rust' },
  { value: 'php', label: 'PHP' },
  { value: 'swift', label: 'Swift' },
  { value: 'kotlin', label: 'Kotlin' },
];

const CodeEditor: React.FC<CodeEditorProps> = ({ 
  onSave, 
  onRun, 
  onClose, 
  onAskForCode, 
  codeSnippets = [],
  onSelectSnippet,
  showHistory = false,
  initialCode = '',
  initialLanguage = 'javascript',
  onInsertSnippetToChat // NEW PROP
}) => {
  const [code, setCode] = useState(initialCode);
  const [language, setLanguage] = useState(initialLanguage);
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(Boolean(initialCode));
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const codeContainerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Track selected snippet from history
  const [selectedSnippetId, setSelectedSnippetId] = useState<string | null>(null);
  const selectedSnippet = selectedSnippetId
    ? codeSnippets.find(s => s.id === selectedSnippetId)
    : null;

  useEffect(() => {
    if (initialCode) {
      setCode(initialCode);
    }
    if (initialLanguage) {
      setLanguage(initialLanguage);
    }
  }, [initialCode, initialLanguage]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  // When a snippet is selected from history, update code/language and track selection
  const handleSelectSnippet = (snippetId: string) => {
    setSelectedSnippetId(snippetId);
    if (onSelectSnippet) {
      onSelectSnippet(snippetId);
    }
    const snippet = codeSnippets.find(s => s.id === snippetId);
    if (snippet) {
      setCode(snippet.code);
      setLanguage(snippet.language);
      setIsEditing(false);
    }
  };

  // Insert selected snippet into chat
  const handleInsertToChat = () => {
    if (selectedSnippet && onInsertSnippetToChat) {
      onInsertSnippetToChat(selectedSnippet);
      setSelectedSnippetId(null);
      if (onClose) onClose();
    }
  };

  // ... rest of the code remains unchanged ...

  const handleSave = () => {
    if (code.trim()) {
      onSave(code, language);
    }
  };

  const executeJavaScriptCode = (jsCode: string): Promise<string> => {
    return new Promise((resolve) => {
      const logs: string[] = [];
      const originalConsoleLog = console.log;
      const originalConsoleError = console.error;
      const originalConsoleWarn = console.warn;
      const originalConsoleInfo = console.info;
      console.log = (...args) => {
        originalConsoleLog(...args);
        logs.push(args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' '));
      };
      console.error = (...args) => {
        originalConsoleError(...args);
        logs.push(`ERROR: ${args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ')}`);
      };
      console.warn = (...args) => {
        originalConsoleWarn(...args);
        logs.push(`WARN: ${args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ')}`);
      };
      console.info = (...args) => {
        originalConsoleInfo(...args);
        logs.push(`INFO: ${args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ')}`);
      };
      try {
        const result = eval(jsCode);
        if (result instanceof Promise) {
          result
            .then(res => {
              if (res !== undefined) {
                logs.push(`Return value: ${typeof res === 'object' ? JSON.stringify(res, null, 2) : res}`);
              }
              console.log = originalConsoleLog;
              console.error = originalConsoleError;
              console.warn = originalConsoleWarn;
              console.info = originalConsoleInfo;
              resolve(logs.join('\n'));
            })
            .catch(err => {
              logs.push(`Runtime error: ${err.message}`);
              console.log = originalConsoleLog;
              console.error = originalConsoleError;
              console.warn = originalConsoleWarn;
              console.info = originalConsoleInfo;
              resolve(logs.join('\n'));
            });
        } else {
          if (result !== undefined) {
            logs.push(`Return value: ${typeof result === 'object' ? JSON.stringify(result, null, 2) : result}`);
          }
          console.log = originalConsoleLog;
          console.error = originalConsoleError;
          console.warn = originalConsoleWarn;
          console.info = originalConsoleInfo;
          resolve(logs.join('\n'));
        }
      } catch (error) {
        logs.push(`Execution error: ${error.message}`);
        console.log = originalConsoleLog;
        console.error = originalConsoleError;
        console.warn = originalConsoleWarn;
        console.info = originalConsoleInfo;
        resolve(logs.join('\n'));
      }
    });
  };

  const handleRun = async () => {
    if (!code.trim()) return;
    setIsRunning(true);
    setOutput(null);
    setError(null);
    try {
      if (onRun) {
        await onRun(code, language);
      }
      if (language === 'javascript' || language === 'typescript') {
        const codeToExecute = language === 'typescript' 
          ? code.replace(/const\s+(\w+)\s*:\s*\w+/g, 'const $1')
               .replace(/let\s+(\w+)\s*:\s*\w+/g, 'let $1')
               .replace(/function\s+\w+\s*\((.*?)\)\s*:\s*\w+/g, 'function($1)')
          : code;
        const result = await executeJavaScriptCode(codeToExecute);
        setOutput(result || 'Code executed successfully (no output)');
      } else {
        setTimeout(() => {
          setOutput(`Code execution for ${language} is simulated in this demo.`);
        }, 1000);
      }
    } catch (err) {
      setError(`Error executing code: ${err.message}`);
      toast({
        title: "Execution Error",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setIsRunning(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Code copied",
      description: "The code has been copied to your clipboard",
    });
  };

  const handleSubmitPrompt = async () => {
    if (!prompt.trim() || !onAskForCode) return;
    setIsGenerating(true);
    try {
      const generatedCode = await onAskForCode(prompt, language);
      console.log("[CodeEditor] AI generated code:", generatedCode);
      setCode(generatedCode);
      setShowPrompt(false);
      setPrompt('');
      toast({
        title: "Code generated",
        description: "The code has been generated based on your request",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = code.substring(0, start) + '  ' + code.substring(end);
      setCode(newValue);
      setTimeout(() => {
        if (textarea) {
          textarea.selectionStart = textarea.selectionEnd = start + 2;
        }
      }, 0);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-4">
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => (
                <SelectItem key={lang.value} value={lang.value}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={copyCode}
            disabled={!code.trim()}
          >
            <Copy size={16} />
            Copy
          </Button>
          {onRun && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={handleRun}
              disabled={!code.trim() || isRunning}
            >
              <Play size={16} />
              {isRunning ? 'Running...' : 'Run'}
            </Button>
          )}
          {onAskForCode && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => setShowPrompt(!showPrompt)}
            >
              <MessageSquare size={16} />
              Ask AI
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'View' : 'Edit'}
          </Button>
        </div>
        <Button onClick={handleSave} disabled={!code.trim()} className="gap-1">
          <Send size={16} />
          Save
        </Button>
        {/* Insert into Chat button, only if a snippet is selected and onInsertSnippetToChat is provided */}
        {selectedSnippet && onInsertSnippetToChat && (
          <Button
            variant="default"
            size="sm"
            className="ml-2 gap-1"
            onClick={handleInsertToChat}
          >
            <FileCode size={16} />
            Insert into Chat
          </Button>
        )}
      </div>
      {showPrompt && (
        <div className="p-4 border-b bg-muted/20">
          <div className="flex gap-2">
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the code you want to generate..."
              className="flex-1 min-h-[80px]"
              disabled={isGenerating}
            />
            <Button 
              onClick={handleSubmitPrompt} 
              disabled={!prompt.trim() || isGenerating}
              className="self-end"
            >
              {isGenerating ? 'Generating...' : 'Generate'}
            </Button>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-hidden grid" style={{ 
        gridTemplateColumns: showHistory ? '1fr 300px' : '1fr',
        gridTemplateRows: output || error ? '1fr auto' : '1fr' 
      }}>
        <div className="overflow-auto p-4 bg-muted/30 relative">
          <div className="font-mono text-sm h-full bg-muted/50 rounded overflow-auto relative">
            {code ? (
              <div className="relative h-full">
                <CodeHighlighter code={stripMarkdownCodeFencing(code)} language={language} />
                {isEditing && (
                  <textarea
                    ref={textareaRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="absolute top-0 left-0 w-full h-full font-mono resize-none text-sm bg-transparent p-4"
                    style={{ 
                      color: 'rgba(0,0,0,0.01)', 
                      caretColor: 'rgb(0, 0, 0)',
                      caretShape: 'block',
                      lineHeight: '1.5',
                    }}
                    placeholder={`// Enter your ${languages.find((l) => l.value === language)?.label || 'code'} here...`}
                  />
                )}
              </div>
            ) : (
              <div className="p-4 text-muted-foreground">
                {`// Enter your ${languages.find((l) => l.value === language)?.label || 'code'} here...`}
              </div>
            )}
          </div>
        </div>
        {showHistory && codeSnippets && (
          <div className="overflow-hidden h-full">
            <CodeHistory 
              codeSnippets={codeSnippets} 
              onSelectSnippet={handleSelectSnippet} 
            />
          </div>
        )}
        {(output || error) && (
          <div className="p-4 border-t overflow-auto" style={{ gridColumn: showHistory ? 'span 2' : '1' }}>
            <h3 className="text-sm font-medium mb-2">Output:</h3>
            <div className={`p-3 rounded-md text-sm font-mono max-h-60 overflow-auto ${error ? 'bg-destructive/10 text-destructive' : 'bg-muted'}`}>
              {error || output}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeEditor;